import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PauseIcon, PlayIcon, SpeakerHighIcon, TrashIcon, UploadSimpleIcon } from '@phosphor-icons/react';
import { SOUND_MAX_DURATION_MS, SOUND_MAX_UPLOAD_BYTES, type Sound } from '@meshalert/contract';
import { ApiError, api } from '../lib/api.ts';
import { absoluteTime } from '../lib/format.ts';
import { PageHeader } from '../components/Layout.tsx';
import { useSession } from '../lib/session.tsx';
import { Dialog } from '../components/Dialog.tsx';
import {
  Button,
  EmptyState,
  ErrorState,
  Field,
  Input,
  ListSkeleton,
  Panel,
  Td,
  Th,
  cx,
} from '../components/ui.tsx';

function seconds(ms: number): string {
  return `${(ms / 1000).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} s`;
}

function fileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`;
}

/** Ein Player für die ganze Seite: ein zweiter Ton hält den ersten an. */
function usePreview() {
  const audio = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => () => audio.current?.pause(), []);

  function stop() {
    audio.current?.pause();
    audio.current = null;
    setPlaying(null);
    setProgress(0);
  }

  function toggle(sound: Sound) {
    if (playing === sound.id) {
      stop();
      return;
    }
    audio.current?.pause();
    const element = new Audio(api.sounds.fileUrl(sound.id));
    element.ontimeupdate = () => setProgress(element.duration ? element.currentTime / element.duration : 0);
    element.onended = () => {
      if (audio.current === element) stop();
    };
    audio.current = element;
    setPlaying(sound.id);
    setProgress(0);
    element.play().catch(() => {
      if (audio.current === element) stop();
    });
  }

  return { playing, progress, toggle };
}

export function SoundsPage() {
  const { can } = useSession();
  const isAdmin = can('admin');
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [renaming, setRenaming] = useState<Sound | null>(null);
  const preview = usePreview();

  const sounds = useQuery({ queryKey: ['sounds'], queryFn: () => api.sounds.list() });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['sounds'] });

  const remove = useMutation({
    mutationFn: (id: string) => api.sounds.remove(id),
    onSuccess: refresh,
    onError: (error) => alert(error instanceof ApiError ? error.message : 'Löschen fehlgeschlagen'),
  });

  const items = sounds.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Töne"
        description="Eigene Töne für kritische Alarme. In der App stehen sie unter Einstellungen bei Ton zur Wahl. Der eingebaute Standardton bleibt die Voreinstellung für jedes Gerät."
        action={
          isAdmin ? (
            <Button variant="primary" icon={<UploadSimpleIcon size={15} />} onClick={() => setUploading(true)}>
              Ton hochladen
            </Button>
          ) : null
        }
      />

      <Panel bodyClassName="p-0">
        {sounds.isLoading ? (
          <ListSkeleton rows={3} />
        ) : sounds.isError ? (
          <ErrorState message="Die Töne konnten nicht geladen werden." onRetry={() => sounds.refetch()} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[42rem] border-collapse">
                <thead className="border-b border-line bg-sunken">
                  <tr>
                    <Th className="w-14">
                      <span className="sr-only">Anhören</span>
                    </Th>
                    <Th>Name</Th>
                    <Th>Dauer</Th>
                    <Th>Größe</Th>
                    <Th>Hochgeladen</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-subtle)]">
                  <tr>
                    <Td>
                      <span className="flex size-8 items-center justify-center text-ink-3">
                        <SpeakerHighIcon size={16} />
                      </span>
                    </Td>
                    <Td>
                      <span className="block text-[13px] font-medium text-ink">Standardton</span>
                      <span className="mt-0.5 block text-[12px] text-ink-3">
                        In der App eingebaut, gilt überall ohne eigene Wahl
                      </span>
                    </Td>
                    <Td />
                    <Td />
                    <Td />
                    <Td />
                  </tr>
                  {items.map((sound) => {
                    const active = preview.playing === sound.id;
                    return (
                      <tr key={sound.id} className="transition-colors hover:bg-hover">
                        <Td>
                          <button
                            type="button"
                            onClick={() => preview.toggle(sound)}
                            aria-label={active ? `${sound.name} anhalten` : `${sound.name} anhören`}
                            className={cx(
                              'flex size-8 items-center justify-center rounded-full border transition-colors active:translate-y-px',
                              active
                                ? 'border-accent bg-accent text-accent-fg'
                                : 'border-line-strong bg-raised text-ink-2 hover:bg-hover hover:text-ink',
                            )}
                          >
                            {active ? <PauseIcon size={13} weight="fill" /> : <PlayIcon size={13} weight="fill" />}
                          </button>
                        </Td>
                        <Td>
                          <span className="block text-[13px] font-medium text-ink">{sound.name}</span>
                          {active ? (
                            <span className="mt-2 block h-0.5 w-40 overflow-hidden rounded-full bg-sunken">
                              <span
                                className="block h-full origin-left bg-accent transition-transform duration-200 ease-linear"
                                style={{ transform: `scaleX(${preview.progress})` }}
                              />
                            </span>
                          ) : (
                            <span className="mt-0.5 block max-w-[22rem] truncate font-mono text-[11px] text-ink-3">
                              {sound.file_name}
                            </span>
                          )}
                        </Td>
                        <Td>
                          <span className="tnum">{seconds(sound.duration_ms)}</span>
                        </Td>
                        <Td>
                          <span className="tnum">{fileSize(sound.size_bytes)}</span>
                        </Td>
                        <Td>{absoluteTime(sound.created_at)}</Td>
                        <Td className="text-right">
                          {isAdmin ? (
                            <span className="flex justify-end gap-1.5">
                              <Button size="sm" onClick={() => setRenaming(sound)}>
                                Umbenennen
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                aria-label={`${sound.name} löschen`}
                                onClick={() => {
                                  if (
                                    confirm(
                                      `Ton "${sound.name}" löschen? Geräte, die ihn gewählt haben, spielen danach wieder den Standardton.`,
                                    )
                                  ) {
                                    remove.mutate(sound.id);
                                  }
                                }}
                              >
                                <TrashIcon size={14} />
                              </Button>
                            </span>
                          ) : null}
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {items.length === 0 ? (
              <div className="border-t border-line">
                <EmptyState
                  icon={<SpeakerHighIcon size={28} />}
                  title="Noch keine eigenen Töne"
                  description={
                    isAdmin
                      ? 'Eine hochgeladene Audiodatei steht danach auf jedem Telefon zur Wahl, sobald dort die App geöffnet wurde.'
                      : 'Eigene Töne lädt ein Admin hoch. Bis dahin spielt jeder kritische Alarm den Standardton.'
                  }
                />
              </div>
            ) : null}
          </>
        )}
      </Panel>

      <p className="mt-4 max-w-[68ch] text-[12px] leading-relaxed text-ink-3">
        Ein Telefon lädt neue Töne, sobald die App geöffnet wird, und meldet sie danach an den Server. Bis
        dahin spielt dort weiter der Standardton, nie ein fehlender. Wird ein Ton gelöscht, fallen alle
        Geräte, die ihn gewählt hatten, auf den Standardton zurück.
      </p>

      {uploading ? (
        <UploadDialog
          onClose={() => setUploading(false)}
          onSaved={() => {
            setUploading(false);
            refresh();
          }}
        />
      ) : null}

      {renaming ? (
        <RenameDialog
          sound={renaming}
          onClose={() => setRenaming(null)}
          onSaved={() => {
            setRenaming(null);
            refresh();
          }}
        />
      ) : null}
    </>
  );
}

function UploadDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [dragging, setDragging] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  function pick(next: File | undefined) {
    if (!next) return;
    if (next.size > SOUND_MAX_UPLOAD_BYTES) {
      setLocalError(`Die Datei ist ${fileSize(next.size)} groß, erlaubt sind ${fileSize(SOUND_MAX_UPLOAD_BYTES)}.`);
      return;
    }
    setLocalError(null);
    setFile(next);
    if (!name.trim()) setName(next.name.replace(/\.[^.]+$/, '').slice(0, 60));
  }

  const save = useMutation({
    mutationFn: () => api.sounds.upload(name.trim(), file!),
    onSuccess: onSaved,
  });

  const apiError = save.error instanceof ApiError ? save.error : null;
  const nameError =
    apiError?.fieldErrors().name ?? (apiError?.code === 'conflict' ? apiError.message : undefined);
  const fileDetail = apiError?.fieldErrors().file;
  const generalError =
    localError ??
    (apiError && !nameError
      ? apiError.status === 413
        ? `Die Datei ist zu groß. Erlaubt sind ${fileSize(SOUND_MAX_UPLOAD_BYTES)}.`
        : apiError.message
      : save.error && !apiError
        ? 'Hochladen fehlgeschlagen'
        : null);

  return (
    <Dialog title="Ton hochladen" onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (file) save.mutate();
        }}
      >
        <Field
          label="Datei"
          hint={`MP3, WAV, M4A, CAF und andere Formate bis ${fileSize(SOUND_MAX_UPLOAD_BYTES)}. Wird in Mono gewandelt und nach ${seconds(SOUND_MAX_DURATION_MS)} abgeschnitten.`}
        >
          {(id) => (
            <label
              htmlFor={id}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                pick(e.dataTransfer.files[0]);
              }}
              className={cx(
                'flex cursor-pointer flex-col items-center gap-1.5 rounded-md border border-dashed px-4 py-6 text-center transition-colors',
                dragging ? 'border-accent bg-hover' : 'border-line-strong bg-sunken hover:bg-hover',
              )}
            >
              <UploadSimpleIcon size={20} className="text-ink-3" />
              {file ? (
                <>
                  <span className="max-w-full truncate text-[13px] font-medium text-ink">{file.name}</span>
                  <span className="text-[12px] text-ink-3">{fileSize(file.size)}, zum Ändern klicken</span>
                </>
              ) : (
                <span className="text-[13px] font-medium text-ink">Datei wählen oder hierher ziehen</span>
              )}
              <input
                id={id}
                type="file"
                accept="audio/*,.caf,.m4a,.aif,.aiff"
                className="sr-only"
                onChange={(e) => pick(e.target.files?.[0])}
              />
            </label>
          )}
        </Field>

        <Field label="Name" hint="So heißt der Ton in der App." error={nameError}>
          {(id) => (
            <Input id={id} required maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />
          )}
        </Field>

        {generalError ? (
          <div role="alert" className="space-y-1">
            <p className="text-[13px] text-critical">{generalError}</p>
            {fileDetail ? <p className="font-mono text-[11px] text-ink-3">{fileDetail}</p> : null}
          </div>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" disabled={!file || !name.trim() || save.isPending}>
            {save.isPending ? 'Wird umgewandelt' : 'Hochladen'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function RenameDialog({ sound, onClose, onSaved }: { sound: Sound; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(sound.name);

  const save = useMutation({
    mutationFn: () => api.sounds.update(sound.id, { name: name.trim() }),
    onSuccess: onSaved,
  });

  const apiError = save.error instanceof ApiError ? save.error : null;
  const nameError = apiError ? (apiError.fieldErrors().name ?? apiError.message) : undefined;

  return (
    <Dialog title="Ton umbenennen" onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <Field label="Name" hint="Die Datei auf den Telefonen bleibt dieselbe." error={nameError}>
          {(id) => (
            <Input id={id} required maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />
          )}
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" disabled={!name.trim() || save.isPending}>
            {save.isPending ? 'Wird gespeichert' : 'Speichern'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
